/**
 * Client-side export of an annotated screenshot (plan §5.4 / D7).
 *
 * A captured, annotated canvas is composited, optionally downscaled so its
 * longest edge is ≤ {@link MAX_LONGEST_EDGE}px, then encoded to WebP (JPEG
 * fallback) at descending quality until it fits under {@link MAX_BYTES}. The
 * server enforces its own type/size limits (`imageUploadService`), so this is
 * a friction-reducing pre-shrink, not the security boundary.
 */

/** Longest-edge cap in device pixels (plan D7). */
export const MAX_LONGEST_EDGE = 2560;

/** Upload byte ceiling — 5 MB (plan D7, matches server limit). */
export const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Fit (width,height) within a square of side `maxDim`, preserving aspect
 * ratio. Returns integer dimensions; never upscales. Pure — unit-tested.
 */
export function fitWithin(
  width: number,
  height: number,
  maxDim: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxDim || longest === 0) {
    return { width: Math.round(width), height: Math.round(height) };
  }
  const scale = maxDim / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Promise wrapper around the callback-style `canvas.toBlob`. */
function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

/**
 * Encode a canvas to a compressed image blob under the size ceiling. Prefers
 * WebP; falls back to JPEG when WebP is unsupported. If the first encode is
 * over {@link MAX_BYTES}, quality is stepped down before giving up.
 *
 * @returns the encoded blob that fits under `maxBytes`, or `null` if the canvas
 * cannot be encoded at all OR cannot be compressed under the ceiling. Callers
 * surface a retry message on `null` rather than uploading bytes the server
 * (imageUploadService, same 5 MB limit) would reject anyway.
 */
export async function encodeUnderLimit(
  canvas: HTMLCanvasElement,
  maxBytes = MAX_BYTES,
): Promise<Blob | null> {
  const qualities = [0.85, 0.7, 0.55, 0.4];
  for (const type of ['image/webp', 'image/jpeg']) {
    for (const quality of qualities) {
      const blob = await toBlob(canvas, type, quality);
      // A browser without WebP support returns a PNG (wrong type) or null —
      // detect the mismatch and move on to JPEG.
      if (!blob || blob.type !== type) break;
      if (blob.size <= maxBytes) return blob;
    }
    // This type's smallest encode is still over the ceiling — try the next type
    // (JPEG) rather than returning an over-limit blob.
  }
  // Nothing fit under the ceiling: signal failure so the caller can prompt a
  // smaller capture instead of silently handing the server an oversized upload.
  return null;
}
